import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  MapPin,
  Video,
  ArrowLeft,
  AlertCircle,
  CalendarPlus,
  LogOut,
} from 'lucide-react';
import { BookingLandingAside, ProfessionalCard } from './BookingLanding';
import { WaitlistForm } from './WaitlistForm';
import {
  createPublicAppointment,
  fetchPublicBookingPage,
  fetchPublicDates,
  fetchPublicTimes,
  type BookingLocation,
  type BookingProfessional,
  type BookingService,
  type PublicBookingPage,
  type PublicSlot,
} from '../services/bookingApi';
import { describeError } from '../utils/describeError';

interface PatientPortalProps {
  slug: string;
}

/**
 * Etapas do fluxo público:
 * profissional → modalidade/local → serviço → dia → horário → dados → pronto.
 * "encerrado" é a tela de despedida quando o navegador não deixa fechar a aba.
 */
type Etapa =
  | 'profissional'
  | 'modalidade'
  | 'servico'
  | 'data'
  | 'horario'
  | 'dados'
  | 'pronto'
  | 'encerrado';

const formatarData = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
};

const toIsoDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (date: Date, days: number) => {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
};

const formatarPreco = (valor?: number) =>
  typeof valor === 'number' && Number.isFinite(valor) && valor > 0
    ? valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;

const modalidadeDoLocal = (local: BookingLocation | null): 'ONLINE' | 'PRESENCIAL' | undefined =>
  local ? (local.type === 'online' ? 'ONLINE' : 'PRESENCIAL') : undefined;

/**
 * Página pública de agendamento — o que o paciente abre pelo link, sem login.
 *
 * Toda a configuração vem da API (`/public/booking/:slug`), então funciona em
 * qualquer dispositivo. Antes esta tela não existia: o link caía no painel do
 * profissional e a configuração morava no localStorage dele.
 */
const PatientPortal: React.FC<PatientPortalProps> = ({ slug }) => {
  const [page, setPage] = useState<PublicBookingPage | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [etapa, setEtapa] = useState<Etapa>('profissional');
  const [profissional, setProfissional] = useState<BookingProfessional | null>(null);
  const [local, setLocal] = useState<BookingLocation | null>(null);
  const [servico, setServico] = useState<BookingService | null>(null);
  const [datas, setDatas] = useState<string[]>([]);
  const [data, setData] = useState<string | null>(null);
  const [slots, setSlots] = useState<PublicSlot[]>([]);
  const [slot, setSlot] = useState<PublicSlot | null>(null);
  const [mesVisivel, setMesVisivel] = useState(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  });
  const [carregandoAgenda, setCarregandoAgenda] = useState(false);

  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [confirmado, setConfirmado] = useState<{ status: string } | null>(null);

  /** Etapas que existem para esta página (depende do que a clínica configurou). */
  const temProfissionais = (page?.professionals.length ?? 0) > 0;

  /**
   * Bloco 29: locais do profissional escolhido. Quem tem configuração própria
   * (sub-aba "Locais de Atendimento" dele) mostra só os seus; sem ela, valem
   * os da página publicada pela clínica.
   */
  const locaisDe = (prof: BookingProfessional | null): BookingLocation[] =>
    prof?.hasOwnConfig ? prof.locations ?? [] : page?.locations ?? [];
  const locaisDisponiveis = useMemo(() => locaisDe(profissional), [page, profissional]);
  const temLocais = locaisDisponiveis.length > 0;

  /** Serviços do profissional escolhido: os dele (bloco 29) ou os da página que ele oferece. */
  const servicosDisponiveis = useMemo(() => {
    if (profissional?.hasOwnConfig) return profissional.services ?? [];
    const all = page?.services ?? [];
    if (!profissional?.serviceIds?.length) return all;
    const allowed = new Set(profissional.serviceIds);
    return all.filter((item) => allowed.has(item.id));
  }, [page, profissional]);

  const etapaInicial = (dados: PublicBookingPage): Etapa => {
    if (dados.professionals.length > 0) return 'profissional';
    if (dados.locations.length > 0) return 'modalidade';
    return 'servico';
  };

  const proximaEtapa = (apos: Etapa, prof: BookingProfessional | null = profissional): Etapa => {
    if (apos === 'profissional') return locaisDe(prof).length > 0 ? 'modalidade' : 'servico';
    if (apos === 'modalidade') return 'servico';
    if (apos === 'servico') return 'data';
    if (apos === 'data') return 'horario';
    if (apos === 'horario') return 'dados';
    return 'pronto';
  };

  const etapaAnterior = (de: Etapa): Etapa => {
    if (de === 'dados') return 'horario';
    if (de === 'horario') return 'data';
    if (de === 'data') return 'servico';
    if (de === 'servico') return temLocais ? 'modalidade' : 'profissional';
    return 'profissional';
  };

  const podeVoltar =
    etapa !== 'pronto' &&
    etapa !== 'encerrado' &&
    etapa !== 'profissional' &&
    !(etapa === 'modalidade' && !temProfissionais) &&
    !(etapa === 'servico' && !temProfissionais && !temLocais);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    fetchPublicBookingPage(slug)
      .then((dados) => {
        if (cancelado) return;
        setPage(dados);
        // `&profissional=<id>` (links "Reagendar" e do bloco #53): já abre na
        // agenda desse profissional; o "Voltar" ainda leva à escolha.
        const preId = new URLSearchParams(window.location.search).get('profissional');
        const pre = preId ? dados.professionals.find((p) => p.id === preId) ?? null : null;
        if (pre) {
          setProfissional(pre);
          // Locais calculados com `dados`: o estado `page` ainda não foi atualizado aqui.
          const locais = pre.hasOwnConfig ? pre.locations ?? [] : dados.locations ?? [];
          setEtapa(locais.length > 0 ? 'modalidade' : 'servico');
        } else {
          setEtapa(etapaInicial(dados));
        }
      })
      .catch((e) => !cancelado && setErro(describeError(e)))
      .finally(() => !cancelado && setCarregando(false));
    return () => {
      cancelado = true;
    };
  }, [slug]);

  // Datas livres do profissional/serviço escolhidos (já descontando bloqueios,
  // compromissos e sessões da agenda interna — a API calcula tudo).
  useEffect(() => {
    if (!page || !servico || etapa !== 'data') return;
    let cancelado = false;
    setCarregandoAgenda(true);
    fetchPublicDates(slug, profissional?.id, servico.id, local?.id)
      .then((r) => !cancelado && setDatas(r.dates))
      .catch((e) => !cancelado && setErro(describeError(e)))
      .finally(() => !cancelado && setCarregandoAgenda(false));
    return () => {
      cancelado = true;
    };
  }, [page, slug, profissional, servico, local, etapa]);

  const escolherData = async (iso: string) => {
    setData(iso);
    setSlot(null);
    setCarregandoAgenda(true);
    try {
      const r = await fetchPublicTimes(slug, iso, profissional?.id, servico?.id, local?.id);
      setSlots(r.slots);
      setEtapa('horario');
    } catch (e) {
      setErro(describeError(e));
    } finally {
      setCarregandoAgenda(false);
    }
  };

  /**
   * Bloco #67: a API já devolve só os horários das faixas que aceitam o local
   * escolhido (Presencial, Online ou Presencial & Online, por dia e faixa).
   */
  const slotsVisiveis = slots;

  const confirmar = async () => {
    if (!data || !slot) return;
    if (nome.trim().length < 2) return setErroEnvio('Informe seu nome completo.');
    if (telefone.trim().length < 8) return setErroEnvio('Informe um telefone para contato.');
    // Bloco 32: o e-mail é exigido para confirmar (o rótulo não diz mais "opcional").
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setErroEnvio('Informe um e-mail válido.');

    setErroEnvio(null);
    setEnviando(true);
    try {
      const criado = await createPublicAppointment(slug, {
        patientName: nome,
        patientPhone: telefone,
        patientEmail: email.trim() || undefined,
        date: data,
        start: slot.start,
        professionalId: profissional?.id,
        serviceId: servico?.id,
        serviceName: servico?.name,
        modality: modalidadeDoLocal(local) ?? slot.modality,
        locationName: local?.name || undefined,
        // Só o que o paciente escreveu; o telefone vai para o cadastro dele.
        notes: observacoes.trim() || undefined,
      });
      setConfirmado({ status: criado.status });
      setEtapa('pronto');
    } catch (e) {
      // Conflito de horário volta aqui: o paciente escolhe outro.
      setErroEnvio(describeError(e));
    } finally {
      setEnviando(false);
    }
  };

  /** "Agendar novo horário": volta ao início com tudo limpo. */
  const novoAgendamento = () => {
    setProfissional(null);
    setLocal(null);
    setServico(null);
    setDatas([]);
    setData(null);
    setSlots([]);
    setSlot(null);
    setNome('');
    setTelefone('');
    setEmail('');
    setObservacoes('');
    setErroEnvio(null);
    setConfirmado(null);
    setEtapa(page ? etapaInicial(page) : 'profissional');
  };

  /**
   * "Sair da página": tenta fechar a aba; navegadores só permitem fechar abas
   * abertas por script, então há uma despedida clara como alternativa.
   */
  const sairDaPagina = () => {
    try {
      window.close();
    } catch {
      /* segue para a despedida */
    }
    window.setTimeout(() => {
      if (!window.closed) setEtapa('encerrado');
    }, 250);
  };


  const datasSet = useMemo(() => new Set(datas), [datas]);
  const hoje = useMemo(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);
  const dataLimite = useMemo(() => {
    const limit = page?.booking.openDaysLimit ?? 30;
    return addDays(hoje, limit);
  }, [hoje, page?.booking.openDaysLimit]);
  const podeMesAnterior = mesVisivel.getFullYear() > hoje.getFullYear() || mesVisivel.getMonth() > hoje.getMonth();
  const proximoMes = new Date(mesVisivel.getFullYear(), mesVisivel.getMonth() + 1, 1);
  const podeProximoMes = proximoMes <= dataLimite;

  const diasDoCalendario = useMemo(() => {
    const inicio = new Date(mesVisivel.getFullYear(), mesVisivel.getMonth(), 1);
    const primeiroDiaSemana = (inicio.getDay() + 6) % 7;
    const cursor = new Date(inicio);
    cursor.setDate(cursor.getDate() - primeiroDiaSemana);
    return Array.from({ length: 42 }, (_, index) => {
      const item = new Date(cursor);
      item.setDate(cursor.getDate() + index);
      const iso = toIsoDate(item);
      const inMonth = item.getMonth() === mesVisivel.getMonth();
      const inWindow = item >= hoje && item <= dataLimite;
      const available = inMonth && inWindow && datasSet.has(iso);
      return { date: item, iso, inMonth, inWindow, available };
    });
  }, [mesVisivel, hoje, dataLimite, datasSet]);
  const clinica = page?.clinic;

  if (carregando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="animate-spin text-indigo-400" size={32} />
      </div>
    );
  }

  if (erro && !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
        <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-[2.5rem] p-5 sm:p-10 text-center shadow-2xl">
          <AlertCircle className="mx-auto text-rose-500 mb-4" size={36} />
          <h1 className="text-xl font-black text-slate-900 dark:text-white">Página não encontrada</h1>
          <p className="text-sm text-slate-500 mt-3 leading-relaxed">{erro}</p>
        </div>
      </div>
    );
  }

  const resumo = [
    data ? formatarData(data) : null,
    slot?.start ?? null,
    profissional?.name ?? null,
    servico?.name ?? null,
    local?.name ?? null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-sans bg-slate-50 dark:bg-slate-950">
      {/* Painel de apresentação da clínica (bloco 35: mesmo componente da pré-visualização do editor) */}
      <BookingLandingAside clinic={clinica} appearance={page?.appearance} />

      {/* Fluxo de agendamento */}
      <main className="flex-1 p-6 md:p-12 overflow-y-auto">
        <div className="max-w-xl mx-auto space-y-8">
          {/* Bloco #64: Modo Recesso — a grade some e entra a lista de espera. */}
          {page?.recess?.enabled ? (
            <WaitlistForm slug={slug} clinicName={clinica?.name || 'a clínica'} recess={page.recess} />
          ) : (
          <>
          {podeVoltar && (
            <button
              onClick={() => setEtapa(etapaAnterior(etapa))}
              className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft size={14} /> Voltar
            </button>
          )}

          {etapa === 'profissional' && (
            <section className="space-y-4">
              <h2 className="text-lg font-black text-slate-900 dark:text-white">Escolha o profissional</h2>
              {page?.professionals.map((prof) => (
                <ProfessionalCard
                  key={prof.id}
                  prof={prof}
                  onSelect={() => {
                    setProfissional(prof);
                    setLocal(null);
                    setServico(null);
                    setData(null);
                    setSlot(null);
                    setEtapa(proximaEtapa('profissional', prof));
                  }}
                />
              ))}
            </section>
          )}

          {etapa === 'modalidade' && (
            <section className="space-y-4">
              <h2 className="text-lg font-black text-slate-900 dark:text-white">
                Escolha a modalidade de atendimento
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Selecione onde você prefere ser atendido.
              </p>
              {/* Opções vêm de Configurações da Agenda > Locais de Atendimento
                  do profissional escolhido (somente os marcados como ativos). */}
              {locaisDisponiveis.map((loc) => {
                const online = loc.type === 'online';
                return (
                  <button
                    key={loc.id}
                    onClick={() => {
                      setLocal(loc);
                      setEtapa(proximaEtapa('modalidade'));
                    }}
                    className="w-full flex items-start gap-4 p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[1.5rem] hover:border-indigo-400 transition-all text-left shadow-sm"
                  >
                    <div
                      className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                        online
                          ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300'
                          : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300'
                      }`}
                    >
                      {online ? <Video size={18} /> : <MapPin size={18} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-black text-sm text-slate-900 dark:text-white">{loc.name}</p>
                        <span
                          className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg shrink-0 ${
                            online
                              ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300'
                              : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300'
                          }`}
                        >
                          {online ? 'Online' : 'Presencial'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium mt-1 leading-relaxed">
                        {online
                          ? 'Atendimento por videochamada. Garanta uma boa conexão de internet e privacidade.'
                          : loc.address || 'Atendimento presencial no consultório.'}
                      </p>
                    </div>
                  </button>
                );
              })}
            </section>
          )}

          {etapa === 'servico' && (
            <section className="space-y-4">
              <h2 className="text-lg font-black text-slate-900 dark:text-white">Escolha o serviço</h2>
              {servicosDisponiveis.length === 0 ? (
                <p className="text-sm text-slate-500 font-medium">Não há serviços disponíveis para este profissional.</p>
              ) : (
                <div className="space-y-3">
                  {servicosDisponiveis.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setServico(item);
                        setData(null);
                        setSlot(null);
                        setSlots([]);
                        setEtapa(proximaEtapa('servico'));
                      }}
                      className="w-full p-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[1.5rem] hover:border-indigo-400 transition-all text-left shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="font-black text-sm text-slate-900 dark:text-white">{item.name}</p>
                          {item.description && <p className="text-[11px] text-slate-500 font-medium mt-1">{item.description}</p>}
                        </div>
                        <span className="shrink-0 text-[10px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 dark:text-indigo-300 px-3 py-1.5 rounded-xl">
                          {item.duration} min
                        </span>
                      </div>
                      {formatarPreco(item.price) && (
                        <p className="text-[11px] text-slate-400 font-bold mt-3">{formatarPreco(item.price)}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {etapa === 'data' && (
            <section className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <CalendarDays size={18} className="text-indigo-500" /> Escolha uma data
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!podeMesAnterior}
                    onClick={() => setMesVisivel(new Date(mesVisivel.getFullYear(), mesVisivel.getMonth() - 1, 1))}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed hover:border-indigo-400"
                    aria-label="Mês anterior"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    disabled={!podeProximoMes}
                    onClick={() => setMesVisivel(proximoMes)}
                    className="p-2 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed hover:border-indigo-400"
                    aria-label="Próximo mês"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest">
                {mesVisivel.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
              </p>
              {carregandoAgenda ? (
                <Loader2 className="animate-spin text-indigo-500" size={24} />
              ) : datas.length === 0 ? (
                <p className="text-sm text-slate-500 font-medium">
                  Não há horários disponíveis no momento. Tente novamente mais tarde.
                </p>
              ) : (
                <div className="rounded-[1.5rem] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 shadow-sm">
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((label) => (
                      <div key={label} className="h-8 flex items-center justify-center text-[10px] font-black uppercase tracking-widest text-slate-400">
                        {label}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {diasDoCalendario.map((item) => (
                      <button
                        key={item.iso}
                        disabled={!item.available}
                        onClick={() => escolherData(item.iso)}
                        className={`aspect-square min-h-11 rounded-xl text-xs font-black transition-all border ${
                          data === item.iso
                            ? 'bg-indigo-600 border-indigo-600 text-white'
                            : item.available
                              ? 'bg-white dark:bg-slate-950 border-emerald-100 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-300 hover:border-indigo-400'
                              : 'bg-slate-50 dark:bg-slate-950/50 border-transparent text-slate-300 dark:text-slate-700 cursor-not-allowed'
                        } ${!item.inMonth ? 'opacity-0 pointer-events-none' : ''}`}
                        aria-label={item.available ? `Selecionar ${formatarData(item.iso)}` : `Data indisponível ${item.iso}`}
                      >
                        {item.date.getDate()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {etapa === 'horario' && (
            <section className="space-y-4">
              <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                <Clock size={18} className="text-indigo-500" /> Escolha o horário
              </h2>
              <p className="text-xs text-slate-500 font-bold capitalize">{data && formatarData(data)}</p>
              {carregandoAgenda ? (
                <Loader2 className="animate-spin text-indigo-500" size={24} />
              ) : slotsVisiveis.length === 0 ? (
                <p className="text-sm text-slate-500 font-medium">Sem horários livres neste dia.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {slotsVisiveis.map((s) => (
                    <button
                      key={s.start}
                      onClick={() => {
                        setSlot(s);
                        setEtapa('dados');
                      }}
                      className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm font-black text-slate-700 dark:text-slate-200 hover:border-indigo-400 hover:text-indigo-600 transition-all flex flex-col items-center gap-1"
                    >
                      <span>{s.start}</span>
                      <span className="text-[10px] font-bold text-slate-400">{s.end}</span>
                      {(modalidadeDoLocal(local) ?? s.modality) === 'ONLINE' ? (
                        <Video size={11} className="text-slate-400" />
                      ) : (
                        <MapPin size={11} className="text-slate-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {etapa === 'dados' && (
            <section className="space-y-5">
              <h2 className="text-lg font-black text-slate-900 dark:text-white">Seus dados</h2>
              <div className="p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-2xl text-xs font-bold text-indigo-700 dark:text-indigo-300 capitalize">
                {resumo}
              </div>

              {[
                { id: 'portal-nome', label: 'Nome completo', value: nome, set: setNome, type: 'text' },
                { id: 'portal-telefone', label: 'Telefone / WhatsApp', value: telefone, set: setTelefone, type: 'tel' },
                { id: 'portal-email', label: 'E-mail', value: email, set: setEmail, type: 'email' },
              ].map((campo) => (
                <div key={campo.id} className="space-y-2">
                  <label htmlFor={campo.id} className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                    {campo.label}
                  </label>
                  <input
                    id={campo.id}
                    type={campo.type}
                    value={campo.value}
                    onChange={(e) => campo.set(e.target.value)}
                    className="w-full p-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm dark:text-white focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400"
                  />
                </div>
              ))}

              <div className="space-y-2">
                <label htmlFor="portal-obs" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Observações (opcional)
                </label>
                <textarea
                  id="portal-obs"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={3}
                  className="w-full p-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm dark:text-white resize-y"
                />
              </div>

              {erroEnvio && (
                <p className="text-xs font-bold text-rose-600 bg-rose-50 dark:bg-rose-950/30 p-4 rounded-2xl">
                  {erroEnvio}
                </p>
              )}

              <button
                onClick={confirmar}
                disabled={enviando}
                className="w-full p-4 bg-indigo-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-indigo-700 disabled:opacity-60 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                {enviando ? <><Loader2 size={16} className="animate-spin" /> Reservando...</> : 'Confirmar agendamento'}
              </button>
            </section>
          )}

          {etapa === 'pronto' && (
            <section className="text-center py-10 space-y-4">
              <div className="w-16 h-16 mx-auto bg-emerald-100 dark:bg-emerald-950/40 rounded-3xl flex items-center justify-center">
                <Check className="text-emerald-600" size={30} />
              </div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white">
                {confirmado?.status === 'CONFIRMADO' ? 'Agendamento confirmado!' : 'Solicitação enviada!'}
              </h2>
              <p className="text-sm text-slate-500 font-medium capitalize">
                {data && formatarData(data)} · {slot?.start}
              </p>
              <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                {confirmado?.status === 'CONFIRMADO'
                  ? 'Seu horário está reservado. Em caso de imprevisto, entre em contato com a clínica.'
                  : 'A clínica vai confirmar seu horário e entrar em contato.'}
              </p>

              {/* Ações pós-confirmação: sem elas o paciente ficava sem saída. */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4 max-w-md mx-auto">
                <button
                  onClick={novoAgendamento}
                  className="flex-1 p-4 bg-indigo-600 text-white rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <CalendarPlus size={16} /> Agendar novo horário
                </button>
                <button
                  onClick={sairDaPagina}
                  className="flex-1 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 rounded-2xl font-black uppercase text-xs tracking-widest hover:border-indigo-400 hover:text-indigo-600 transition-all flex items-center justify-center gap-2"
                >
                  <LogOut size={16} /> Sair da página
                </button>
              </div>
            </section>
          )}

          {etapa === 'encerrado' && (
            <section className="text-center py-10 space-y-4">
              <div className="w-16 h-16 mx-auto bg-indigo-100 dark:bg-indigo-950/40 rounded-3xl flex items-center justify-center">
                <Check className="text-indigo-600" size={30} />
              </div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white">Até breve!</h2>
              <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                Seu agendamento está registrado. Você já pode fechar esta janela com segurança.
              </p>
              <div className="pt-4">
                <button
                  onClick={novoAgendamento}
                  className="text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:underline"
                >
                  Fazer outro agendamento
                </button>
              </div>
            </section>
          )}
          </>
          )}
        </div>
      </main>
    </div>
  );
};

export default PatientPortal;
